/**
 * Installed-mode gate (ARCHITECTURE.md §5, P1.md smoke steps 12–14):
 * in a Safari tab the app is ONLINE-ONLY with a persistent "Install for
 * offline" banner — tab storage lives in a separate container under 7-day ITP,
 * so offline is only ever promised in standalone mode. On first standalone
 * launch, request durable storage and verify it.
 */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari legacy flag (not in lib.dom types).
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export interface PersistenceStatus {
  persisted: boolean;
  /** navigator.storage.estimate() snapshot, when the API exists. */
  usage: number | null;
  quota: number | null;
}

/**
 * Request + verify durable storage. Call ONLY in standalone mode (the gate).
 * Never throws — offline readiness reporting degrades honestly instead.
 */
export async function ensurePersistentStorage(): Promise<PersistenceStatus> {
  let persisted = false;
  let usage: number | null = null;
  let quota: number | null = null;
  try {
    if (navigator.storage?.persist) {
      persisted = (await navigator.storage.persisted()) || (await navigator.storage.persist());
    }
    if (navigator.storage?.estimate) {
      const estimate = await navigator.storage.estimate();
      usage = estimate.usage ?? null;
      quota = estimate.quota ?? null;
    }
  } catch {
    /* report false rather than crash the boot path */
  }
  return { persisted, usage, quota };
}
